import { Platform } from "react-native";
import {
  confirmPasswordReset as confirmPasswordResetFirebase,
  fetchSignInMethodsForEmail,
  getAuth,
  GoogleAuthProvider,
  multiFactor,
  OAuthProvider,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  signInWithEmailAndPassword as signInWithEmailAndPasswordFirebase,
  signInWithEmailLink as signInWithEmailLinkFirebase,
  signInWithPopup,
  signOut,
  updatePassword as updatePasswordFirebase,
  verifyPasswordResetCode as verifyPasswordResetCodeFirebase,
  type ApplicationVerifier,
  type MultiFactorResolver,
} from "firebase/auth";

import { HTTPStatusCode } from "@libs/api/types";
import { i18n } from "@/i18n";
import { getLanguage } from "@/i18n/utils";
import { sendSentryError } from "@/services/sentry";
import { tsRestClient } from "@/services/ts-rest";
import { logger } from "@/utils/logger";

export const AuthApi = {
  getUsernameAccountStatus: async (username: string) => {
    const res = await tsRestClient().auth.getUsernameAccountStatus({
      params: { username },
    });

    if (res.status === HTTPStatusCode.OK) {
      return res.body;
    }

    sendSentryError({
      message: "Error in AuthApi.getUsernameAccountStatus:",
      error: res.body,
    });
  },
  createUsernameAccount: async (username: string, password: string) => {
    const res = await tsRestClient().auth.addUsernameAccount({
      body: { username, password },
    });

    if (res.status === HTTPStatusCode.CONFLICT) {
      return { isUsernameTaken: true };
    }

    if (res.status !== HTTPStatusCode.CREATED) {
      logger.error({
        message: "Error in AuthApi.createUsernameAccount:",
        error: res.body,
        shouldThrowError: true,
      });
    }

    return { isUsernameTaken: false };
  },
  checkAccountStatus: async (email: string) => {
    try {
      const auth = getAuth();
      const methods = await fetchSignInMethodsForEmail(auth, email);
      const hasAccount = methods.length > 0;
      const hasPasswordMethod = methods.includes("password");
      return {
        hasAccount,
        hasPasswordMethod,
        hasAuth: hasAccount && hasPasswordMethod,
      };
    } catch (error) {
      logger.error({
        message: "Error in AuthApi.checkAccountStatus:",
        error,
        shouldThrowError: false,
      });
      return {
        hasAccount: false,
        hasPasswordMethod: false,
        hasAuth: false,
      };
    }
  },
  sendSignInLink: async (email: string, code: string) => {
    // The server generates the Firebase sign-in link and delivers a localized
    // email via SendGrid. Link completion still happens client-side via
    // signInWithEmailLink on the verify screen.
    const res = await tsRestClient().auth.sendSignInLink({
      body: {
        email,
        language: getLanguage(),
        code: code || undefined,
      },
    });

    if (res.status !== HTTPStatusCode.OK) {
      logger.error({
        message: "Error in AuthApi.sendSignInLink:",
        error: res.body,
        shouldThrowError: true,
      });
    }
  },
  sendPasswordResetEmail: async (email: string) => {
    const res = await tsRestClient().auth.sendPasswordResetLink({
      body: {
        email,
        language: getLanguage(),
      },
    });

    if (res.status !== HTTPStatusCode.OK) {
      logger.error({
        message: "Error in AuthApi.sendPasswordResetEmail:",
        error: res.body,
        shouldThrowError: true,
      });
    }
  },
  verifyPasswordResetCode: async (oobCode: string) => {
    const auth = getAuth();
    return verifyPasswordResetCodeFirebase(auth, oobCode);
  },
  confirmPasswordReset: async (oobCode: string, newPassword: string) => {
    const auth = getAuth();
    return confirmPasswordResetFirebase(auth, oobCode, newPassword);
  },
  signOut: () => {
    const auth = getAuth();
    return signOut(auth);
  },
  signInWithEmailLink: async (email: string) => {
    const auth = getAuth();
    return signInWithEmailLinkFirebase(auth, email, window.location.href);
  },
  signInWithEmailAndPassword: async (email: string, password: string) => {
    const auth = getAuth();
    return signInWithEmailAndPasswordFirebase(auth, email, password);
  },
  signInWithGoogle: async () => {
    if (Platform.OS !== "web") {
      logger.error({
        message: "Error in AuthApi.signInWithGoogle:",
        error: new Error(i18n.t("common:errors.googleSsoNotSupported")),
        shouldThrowError: true,
      });
      return;
    }
    const auth = getAuth();
    const googleProvider = new GoogleAuthProvider();
    googleProvider.addScope("email");
    googleProvider.addScope("profile");
    return signInWithPopup(auth, googleProvider);
  },
  signInWithApple: async () => {
    if (Platform.OS !== "web") {
      logger.error({
        message: "Error in AuthApi.signInWithApple:",
        error: new Error(i18n.t("common:errors.appleSsoNotSupported")),
        shouldThrowError: true,
      });
      return;
    }
    const auth = getAuth();
    const appleProvider = new OAuthProvider("apple.com");
    appleProvider.addScope("email");
    appleProvider.addScope("name");
    return signInWithPopup(auth, appleProvider);
  },
  updatePassword: async (password: string) => {
    const auth = getAuth();
    if (!auth.currentUser) {
      logger.error({
        message: "Error in AuthApi.updatePassword:",
        error: new Error(i18n.t("common:errors.unauthenticated")),
        shouldThrowError: true,
      });
      return;
    }
    return updatePasswordFirebase(auth.currentUser, password);
  },
  sendMfaVerificationCode: async ({
    phoneNumber,
    applicationVerifier,
    resolver,
  }: {
    phoneNumber: string;
    applicationVerifier: ApplicationVerifier;
    resolver?: MultiFactorResolver;
  }) => {
    const auth = getAuth();
    const phoneAuthProvider = new PhoneAuthProvider(auth);

    if (resolver) {
      // Sign-in verification flow
      const hint = resolver.hints[0];
      return phoneAuthProvider.verifyPhoneNumber(
        {
          multiFactorHint: hint,
          session: resolver.session,
        },
        applicationVerifier,
      );
    } else {
      // Enrollment flow
      if (!auth.currentUser) {
        logger.error({
          message: "Error in AuthApi.sendMfaVerificationCode:",
          error: new Error("User must be logged in to enroll in MFA"),
          shouldThrowError: true,
        });
        return;
      }
      const session = await multiFactor(auth.currentUser).getSession();
      return phoneAuthProvider.verifyPhoneNumber(
        {
          phoneNumber,
          session,
        },
        applicationVerifier,
      );
    }
  },
  enrollMfa: async ({
    verificationId,
    code,
    displayName,
  }: {
    verificationId: string;
    code: string;
    displayName?: string;
  }) => {
    const auth = getAuth();
    if (!auth.currentUser) {
      logger.error({
        message: "Error in AuthApi.enrollMfa:",
        error: new Error("User must be logged in to enroll in MFA"),
        shouldThrowError: true,
      });
      return;
    }
    const credential = PhoneAuthProvider.credential(verificationId, code);
    const assertion = PhoneMultiFactorGenerator.assertion(credential);
    return multiFactor(auth.currentUser).enroll(
      assertion,
      displayName ?? i18n.t("native:mfaPhoneScreen.phoneNumberLabel"),
    );
  },
  verifyMfaSignIn: async ({
    resolver,
    verificationId,
    code,
  }: {
    resolver: MultiFactorResolver;
    verificationId: string;
    code: string;
  }) => {
    const credential = PhoneAuthProvider.credential(verificationId, code);
    const assertion = PhoneMultiFactorGenerator.assertion(credential);
    return resolver.resolveSignIn(assertion);
  },
};
